/**
 * FormBuilder Context
 * 
 * Provides state management for the form builder UI including:
 * - Form data and metadata
 * - Selection state (step, variant, field)
 * - Dirty state tracking
 * - Auto-save functionality
 * 
 * HIPAA COMPLIANCE NOTES:
 * - Form field labels and metadata are not PHI
 * - PHI is only in submission data, not form definitions
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import type {
  Form,
  FormStep,
  StepVariant,
  FormField,
  StepCreateRequest,
  VariantCreateRequest,
  FieldCreateRequest,
} from '@formflow/shared';
import { formsApi, stepsApi, variantsApi, fieldsApi } from '../lib/api';

// =============================================================================
// Types
// =============================================================================

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface FormBuilderState {
  // Form data
  form: Form | null;
  isLoading: boolean;
  loadError: string | null;

  // Selection
  selectedStepId: string | null;
  selectedVariantId: string | null;
  selectedFieldId: string | null;

  // Edit state
  isDirty: boolean;
  saveStatus: SaveStatus;
  saveError: string | null;

  // UI state
  isPreviewOpen: boolean;
  isFieldTypeSelectorOpen: boolean;
  isPublishModalOpen: boolean;
}

type FormBuilderAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_FORM'; payload: Form }
  | { type: 'SET_LOAD_ERROR'; payload: string }
  | { type: 'SELECT_STEP'; payload: string | null }
  | { type: 'SELECT_VARIANT'; payload: string | null }
  | { type: 'SELECT_FIELD'; payload: string | null }
  | { type: 'SET_DIRTY'; payload: boolean }
  | { type: 'SET_SAVE_STATUS'; payload: SaveStatus }
  | { type: 'SET_SAVE_ERROR'; payload: string | null }
  | { type: 'UPDATE_FORM'; payload: Partial<Form> }
  | { type: 'UPDATE_STEP'; payload: { stepId: string; updates: Partial<FormStep> } }
  | { type: 'UPDATE_VARIANT'; payload: { variantId: string; updates: Partial<StepVariant> } }
  | { type: 'UPDATE_FIELD'; payload: { fieldId: string; updates: Partial<FormField> } }
  | { type: 'ADD_STEP'; payload: FormStep }
  | { type: 'REMOVE_STEP'; payload: string }
  | { type: 'ADD_VARIANT'; payload: { stepId: string; variant: StepVariant } }
  | { type: 'REMOVE_VARIANT'; payload: { stepId: string; variantId: string } }
  | { type: 'ADD_FIELD'; payload: { variantId: string; field: FormField } }
  | { type: 'REMOVE_FIELD'; payload: { variantId: string; fieldId: string } }
  | { type: 'REORDER_STEPS'; payload: FormStep[] }
  | { type: 'REORDER_FIELDS'; payload: { variantId: string; fields: FormField[] } }
  | { type: 'TOGGLE_PREVIEW'; payload: boolean }
  | { type: 'TOGGLE_FIELD_TYPE_SELECTOR'; payload: boolean }
  | { type: 'TOGGLE_PUBLISH_MODAL'; payload: boolean };

interface FormBuilderContextValue {
  state: FormBuilderState;
  
  // Data operations
  loadForm: (formId: string) => Promise<void>;
  saveForm: () => Promise<void>;
  publishForm: (changelog?: string) => Promise<void>;
  
  // Selection
  selectStep: (stepId: string | null) => void;
  selectVariant: (variantId: string | null) => void;
  selectField: (fieldId: string | null) => void;
  
  // Form operations
  updateFormSettings: (updates: Partial<Form>) => void;
  
  // Step operations
  addStep: (data: StepCreateRequest) => Promise<FormStep>;
  updateStep: (stepId: string, updates: Partial<StepCreateRequest>) => Promise<void>;
  deleteStep: (stepId: string) => Promise<void>;
  reorderSteps: (stepIds: string[]) => Promise<void>;
  
  // Variant operations
  addVariant: (stepId: string, data: VariantCreateRequest) => Promise<StepVariant>;
  updateVariant: (variantId: string, updates: Partial<VariantCreateRequest>) => Promise<void>;
  deleteVariant: (variantId: string) => Promise<void>;
  
  // Field operations
  addField: (variantId: string, data: FieldCreateRequest) => Promise<FormField>;
  updateField: (fieldId: string, updates: Partial<FieldCreateRequest>) => Promise<void>;
  deleteField: (fieldId: string) => Promise<void>;
  reorderFields: (variantId: string, fieldIds: string[]) => Promise<void>;
  
  // UI operations
  togglePreview: (open: boolean) => void;
  toggleFieldTypeSelector: (open: boolean) => void;
  togglePublishModal: (open: boolean) => void;
  
  // Helpers
  getSelectedStep: () => FormStep | null;
  getSelectedVariant: () => StepVariant | null;
  getSelectedField: () => FormField | null;
}

// =============================================================================
// Initial State
// =============================================================================

const initialState: FormBuilderState = {
  form: null,
  isLoading: true,
  loadError: null,
  selectedStepId: null,
  selectedVariantId: null,
  selectedFieldId: null,
  isDirty: false,
  saveStatus: 'idle',
  saveError: null,
  isPreviewOpen: false,
  isFieldTypeSelectorOpen: false,
  isPublishModalOpen: false,
};

// =============================================================================
// Reducer
// =============================================================================

function reducer(state: FormBuilderState, action: FormBuilderAction): FormBuilderState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_FORM':
      return {
        ...state,
        form: action.payload,
        isLoading: false,
        loadError: null,
        isDirty: false,
        saveStatus: 'idle',
      };

    case 'SET_LOAD_ERROR':
      return { ...state, loadError: action.payload, isLoading: false };

    case 'SELECT_STEP':
      return {
        ...state,
        selectedStepId: action.payload,
        selectedVariantId: null, // Reset variant selection
        selectedFieldId: null, // Reset field selection
      };

    case 'SELECT_VARIANT':
      return {
        ...state,
        selectedVariantId: action.payload,
        selectedFieldId: null, // Reset field selection
      };

    case 'SELECT_FIELD':
      return { ...state, selectedFieldId: action.payload };

    case 'SET_DIRTY':
      return { ...state, isDirty: action.payload };

    case 'SET_SAVE_STATUS':
      return { ...state, saveStatus: action.payload };

    case 'SET_SAVE_ERROR':
      return { ...state, saveError: action.payload };

    case 'UPDATE_FORM':
      if (!state.form) return state;
      return {
        ...state,
        form: { ...state.form, ...action.payload },
        isDirty: true,
      };

    case 'UPDATE_STEP': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.map((step) =>
        step.id === action.payload.stepId
          ? { ...step, ...action.payload.updates }
          : step
      );
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        isDirty: true,
      };
    }

    case 'UPDATE_VARIANT': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.map((step) => ({
        ...step,
        variants: step.variants.map((variant) =>
          variant.id === action.payload.variantId
            ? { ...variant, ...action.payload.updates }
            : variant
        ),
      }));
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        isDirty: true,
      };
    }

    case 'UPDATE_FIELD': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.map((step) => ({
        ...step,
        variants: step.variants.map((variant) => ({
          ...variant,
          fields: variant.fields.map((field) =>
            field.id === action.payload.fieldId
              ? { ...field, ...action.payload.updates }
              : field
          ),
        })),
      }));
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        isDirty: true,
      };
    }

    case 'ADD_STEP': {
      if (!state.form?.active_version) return state;
      return {
        ...state,
        form: {
          ...state.form,
          active_version: {
            ...state.form.active_version,
            steps: [...state.form.active_version.steps, action.payload],
          },
        },
        selectedStepId: action.payload.id,
        selectedVariantId: action.payload.variants[0]?.id || null,
        isDirty: true,
      };
    }

    case 'REMOVE_STEP': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.filter(
        (step) => step.id !== action.payload
      );
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        selectedStepId: state.selectedStepId === action.payload ? null : state.selectedStepId,
        isDirty: true,
      };
    }

    case 'ADD_VARIANT': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.map((step) =>
        step.id === action.payload.stepId
          ? { ...step, variants: [...step.variants, action.payload.variant] }
          : step
      );
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        selectedVariantId: action.payload.variant.id,
        isDirty: true,
      };
    }

    case 'REMOVE_VARIANT': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.map((step) =>
        step.id === action.payload.stepId
          ? {
              ...step,
              variants: step.variants.filter((v) => v.id !== action.payload.variantId),
            }
          : step
      );
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        selectedVariantId:
          state.selectedVariantId === action.payload.variantId ? null : state.selectedVariantId,
        isDirty: true,
      };
    }

    case 'ADD_FIELD': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.map((step) => ({
        ...step,
        variants: step.variants.map((variant) =>
          variant.id === action.payload.variantId
            ? { ...variant, fields: [...variant.fields, action.payload.field] }
            : variant
        ),
      }));
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        selectedFieldId: action.payload.field.id,
        isDirty: true,
      };
    }

    case 'REMOVE_FIELD': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.map((step) => ({
        ...step,
        variants: step.variants.map((variant) =>
          variant.id === action.payload.variantId
            ? {
                ...variant,
                fields: variant.fields.filter((f) => f.id !== action.payload.fieldId),
              }
            : variant
        ),
      }));
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        selectedFieldId:
          state.selectedFieldId === action.payload.fieldId ? null : state.selectedFieldId,
        isDirty: true,
      };
    }

    case 'REORDER_STEPS': {
      if (!state.form?.active_version) return state;
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: action.payload },
        },
        isDirty: true,
      };
    }

    case 'REORDER_FIELDS': {
      if (!state.form?.active_version?.steps) return state;
      const updatedSteps = state.form.active_version.steps.map((step) => ({
        ...step,
        variants: step.variants.map((variant) =>
          variant.id === action.payload.variantId
            ? { ...variant, fields: action.payload.fields }
            : variant
        ),
      }));
      return {
        ...state,
        form: {
          ...state.form,
          active_version: { ...state.form.active_version, steps: updatedSteps },
        },
        isDirty: true,
      };
    }

    case 'TOGGLE_PREVIEW':
      return { ...state, isPreviewOpen: action.payload };

    case 'TOGGLE_FIELD_TYPE_SELECTOR':
      return { ...state, isFieldTypeSelectorOpen: action.payload };

    case 'TOGGLE_PUBLISH_MODAL':
      return { ...state, isPublishModalOpen: action.payload };

    default:
      return state;
  }
}

// =============================================================================
// Context
// =============================================================================

const FormBuilderContext = createContext<FormBuilderContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface FormBuilderProviderProps {
  children: React.ReactNode;
  formId: string;
}

export function FormBuilderProvider({ children, formId }: FormBuilderProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const autoSaveTimer = useRef<NodeJS.Timeout | null>(null);

  // Load form on mount
  const loadForm = useCallback(async (id: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const form = await formsApi.get(id);
      dispatch({ type: 'SET_FORM', payload: form });

      // Auto-select first step and variant
      if (form.active_version?.steps?.[0]) {
        const firstStep = form.active_version.steps[0];
        dispatch({ type: 'SELECT_STEP', payload: firstStep.id });
        if (firstStep.variants?.[0]) {
          dispatch({ type: 'SELECT_VARIANT', payload: firstStep.variants[0].id });
        }
      }
    } catch (error) {
      dispatch({ type: 'SET_LOAD_ERROR', payload: 'Failed to load form' });
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadForm(formId);
  }, [formId, loadForm]);

  // Auto-save when dirty
  useEffect(() => {
    if (state.isDirty && state.form) {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
      autoSaveTimer.current = setTimeout(() => {
        saveFormInternal();
      }, 2000);
    }

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [state.isDirty, state.form]);

  // Save form to API
  const saveFormInternal = async () => {
    if (!state.form) return;

    dispatch({ type: 'SET_SAVE_STATUS', payload: 'saving' });
    try {
      await formsApi.update(state.form.id, {
        name: state.form.name,
        description: state.form.description,
        embed_config: state.form.embed_config,
      });
      dispatch({ type: 'SET_SAVE_STATUS', payload: 'saved' });
      dispatch({ type: 'SET_DIRTY', payload: false });

      // Reset to idle after 2 seconds
      setTimeout(() => {
        dispatch({ type: 'SET_SAVE_STATUS', payload: 'idle' });
      }, 2000);
    } catch (error) {
      dispatch({ type: 'SET_SAVE_STATUS', payload: 'error' });
      dispatch({ type: 'SET_SAVE_ERROR', payload: 'Failed to save form' });
    }
  };

  const saveForm = async () => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
    }
    await saveFormInternal();
  };

  const publishForm = async (changelog?: string) => {
    if (!state.form) return;

    dispatch({ type: 'SET_SAVE_STATUS', payload: 'saving' });
    try {
      await formsApi.publish(state.form.id, { changelog });
      // Reload form to get updated version
      await loadForm(state.form.id);
      dispatch({ type: 'TOGGLE_PUBLISH_MODAL', payload: false });
    } catch (error) {
      dispatch({ type: 'SET_SAVE_ERROR', payload: 'Failed to publish form' });
    }
  };

  // Selection handlers
  const selectStep = (stepId: string | null) => {
    dispatch({ type: 'SELECT_STEP', payload: stepId });
    
    // Auto-select first variant of the step
    if (stepId && state.form?.active_version?.steps) {
      const step = state.form.active_version.steps.find(s => s.id === stepId);
      if (step?.variants?.[0]) {
        dispatch({ type: 'SELECT_VARIANT', payload: step.variants[0].id });
      }
    }
  };

  const selectVariant = (variantId: string | null) => {
    dispatch({ type: 'SELECT_VARIANT', payload: variantId });
  };

  const selectField = (fieldId: string | null) => {
    dispatch({ type: 'SELECT_FIELD', payload: fieldId });
  };

  // Form operations
  const updateFormSettings = (updates: Partial<Form>) => {
    dispatch({ type: 'UPDATE_FORM', payload: updates });
  };

  // Step operations
  const addStep = async (data: StepCreateRequest): Promise<FormStep> => {
    if (!state.form) throw new Error('No form loaded');
    const step = await stepsApi.create(state.form.id, data);
    
    // Reload the form to get the complete step structure from the backend
    // This ensures we have all IDs, variants, and nested relationships correct
    await loadForm(state.form.id);
    
    return step;
  };

  const updateStep = async (stepId: string, updates: Partial<StepCreateRequest>) => {
    await stepsApi.update(stepId, updates);
    dispatch({ type: 'UPDATE_STEP', payload: { stepId, updates } });
  };

  const deleteStep = async (stepId: string) => {
    await stepsApi.delete(stepId);
    dispatch({ type: 'REMOVE_STEP', payload: stepId });
  };

  const reorderSteps = async (stepIds: string[]) => {
    if (!state.form) return;
    await stepsApi.reorder(state.form.id, { order: stepIds });
    
    // Reorder in local state
    const steps = state.form.active_version?.steps || [];
    const reorderedSteps = stepIds
      .map((id) => steps.find((s) => s.id === id))
      .filter(Boolean) as FormStep[];
    dispatch({ type: 'REORDER_STEPS', payload: reorderedSteps });
  };

  // Variant operations
  const addVariant = async (stepId: string, data: VariantCreateRequest): Promise<StepVariant> => {
    const variant = await variantsApi.create(stepId, data);
    dispatch({ type: 'ADD_VARIANT', payload: { stepId, variant } });
    return variant;
  };

  const updateVariant = async (variantId: string, updates: Partial<VariantCreateRequest>) => {
    await variantsApi.update(variantId, updates);
    dispatch({ type: 'UPDATE_VARIANT', payload: { variantId, updates } });
  };

  const deleteVariant = async (variantId: string) => {
    // Find the step containing this variant
    const step = state.form?.active_version?.steps.find((s) =>
      s.variants.some((v) => v.id === variantId)
    );
    if (!step) return;

    await variantsApi.delete(variantId);
    dispatch({ type: 'REMOVE_VARIANT', payload: { stepId: step.id, variantId } });
  };

  // Field operations
  const addField = async (variantId: string, data: FieldCreateRequest): Promise<FormField> => {
    const field = await fieldsApi.create(variantId, data);
    dispatch({ type: 'ADD_FIELD', payload: { variantId, field } });
    return field;
  };

  const updateField = async (fieldId: string, updates: Partial<FieldCreateRequest>) => {
    await fieldsApi.update(fieldId, updates);
    dispatch({ type: 'UPDATE_FIELD', payload: { fieldId, updates } });
  };

  const deleteField = async (fieldId: string) => {
    // Find the variant containing this field
    let targetVariantId: string | null = null;
    state.form?.active_version?.steps.forEach((step) => {
      step.variants.forEach((variant) => {
        if (variant.fields.some((f) => f.id === fieldId)) {
          targetVariantId = variant.id;
        }
      });
    });

    if (!targetVariantId) return;

    await fieldsApi.delete(fieldId);
    dispatch({ type: 'REMOVE_FIELD', payload: { variantId: targetVariantId, fieldId } });
  };

  const reorderFields = async (variantId: string, fieldIds: string[]) => {
    await fieldsApi.reorder(variantId, { order: fieldIds });

    // Reorder in local state
    const variant = state.form?.active_version?.steps
      .flatMap((s) => s.variants)
      .find((v) => v.id === variantId);

    if (variant) {
      const reorderedFields = fieldIds
        .map((id) => variant.fields.find((f) => f.id === id))
        .filter(Boolean) as FormField[];
      dispatch({ type: 'REORDER_FIELDS', payload: { variantId, fields: reorderedFields } });
    }
  };

  // UI operations
  const togglePreview = (open: boolean) => {
    dispatch({ type: 'TOGGLE_PREVIEW', payload: open });
  };

  const toggleFieldTypeSelector = (open: boolean) => {
    dispatch({ type: 'TOGGLE_FIELD_TYPE_SELECTOR', payload: open });
  };

  const togglePublishModal = (open: boolean) => {
    dispatch({ type: 'TOGGLE_PUBLISH_MODAL', payload: open });
  };

  // Helpers
  const getSelectedStep = (): FormStep | null => {
    if (!state.selectedStepId || !state.form?.active_version?.steps) return null;
    return state.form.active_version.steps.find((s) => s.id === state.selectedStepId) || null;
  };

  const getSelectedVariant = (): StepVariant | null => {
    if (!state.selectedVariantId) return null;
    const step = getSelectedStep();
    if (!step) return null;
    return step.variants.find((v) => v.id === state.selectedVariantId) || null;
  };

  const getSelectedField = (): FormField | null => {
    if (!state.selectedFieldId) return null;
    const variant = getSelectedVariant();
    if (!variant) return null;
    return variant.fields.find((f) => f.id === state.selectedFieldId) || null;
  };

  const value: FormBuilderContextValue = {
    state,
    loadForm,
    saveForm,
    publishForm,
    selectStep,
    selectVariant,
    selectField,
    updateFormSettings,
    addStep,
    updateStep,
    deleteStep,
    reorderSteps,
    addVariant,
    updateVariant,
    deleteVariant,
    addField,
    updateField,
    deleteField,
    reorderFields,
    togglePreview,
    toggleFieldTypeSelector,
    togglePublishModal,
    getSelectedStep,
    getSelectedVariant,
    getSelectedField,
  };

  return (
    <FormBuilderContext.Provider value={value}>
      {children}
    </FormBuilderContext.Provider>
  );
}

// =============================================================================
// Hooks
// =============================================================================

export function useFormBuilder(): FormBuilderContextValue {
  const context = useContext(FormBuilderContext);
  if (!context) {
    throw new Error('useFormBuilder must be used within a FormBuilderProvider');
  }
  return context;
}

export function useFormBuilderState(): FormBuilderState {
  return useFormBuilder().state;
}

export function useSelectedStep(): FormStep | null {
  const { getSelectedStep } = useFormBuilder();
  return getSelectedStep();
}

export function useSelectedVariant(): StepVariant | null {
  const { getSelectedVariant } = useFormBuilder();
  return getSelectedVariant();
}

export function useSelectedField(): FormField | null {
  const { getSelectedField } = useFormBuilder();
  return getSelectedField();
}
